import * as Account from "../utils/account";

export function init() {
  const navEl = document.querySelector(".app-nav");
  if (navEl === null) return;

  moveNavMenusToSafePlace(navEl);
  initCursorSafeArea(navEl);
  initMouseNavigation(navEl);
  initKeyboardNavigation(navEl);
  initActiveSessions();
  initMobileNav();
}

function initMobileNav() {
  document.querySelectorAll(".js-nav-mobile-auto-item").forEach((itemEl) => {
    const iconEl = itemEl.querySelector(".js-nav-mobile-icon");

    iconEl.addEventListener("click", () => {
      const isOpen = toggleItemOpen(itemEl);
      if (isOpen) {
        // If the item is open, listen for clicks outside of it to close it
        const menuEl = itemEl.querySelector(".js-nav-mobile-menu");

        const clickOutsideListener = (e) => {
          if (menuEl.contains(e.target)) return;
          toggleItemOpen(itemEl, false);
          document.removeEventListener("click", clickOutsideListener);
        };

        requestAnimationFrame(() => {
          // Wait for the click event to propagate before adding the listener
          document.addEventListener("click", clickOutsideListener);
        });
      }
    });
  });
}

/**
 * Return the trigger button of an app-nav item.
 *
 * @param {HTMLElement} itemEl
 * @returns {HTMLButtonElement | null}
 */
function getTrigger(itemEl) {
  return itemEl.querySelector(".app-nav__trigger");
}

/**
 * Focus the first interactive link inside the popover of an item.
 *
 * @param {HTMLElement} itemEl
 */
function focusFirstLinkInMenu(itemEl) {
  const target = itemEl.querySelector(".app-nav__menu .button, .app-nav__menu a, .app-nav__menu button");
  target?.focus();
}

function moveNavMenusToSafePlace(navEl) {
  const menuEls = navEl.querySelectorAll(".app-nav__menu");

  for (const menuEl of menuEls) {
    const { top, bottom } = menuEl.getBoundingClientRect();
    const availableHeight = window.innerHeight;

    if (top > 0 && bottom < availableHeight) {
      continue;
    }

    menuEl.style.position = "fixed";
    menuEl.style.bottom = "1.5rem";
    menuEl.style.top = bottom > top + availableHeight ? "1.5rem" : "auto";
  }
}

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Enable debug mode to see the safe area for the cursor to move in.
 */
const DEBUG_SAFE_AREA = false;

/**
 * When the user hovers the menu, create a safe area for the cursor to be in.
 *
 * Inspired by https://www.smashingmagazine.com/2023/08/better-context-menus-safe-triangles/
 *
 * @param {HTMLElement} navEl
 */
function initCursorSafeArea(navEl) {
  let svgEl = document.createElementNS(SVG_NS, "svg");
  svgEl.style.position = "fixed";
  svgEl.style.pointerEvents = "none";
  svgEl.style.zIndex = "2";

  let pathEl = document.createElementNS(SVG_NS, "path");
  pathEl.style.pointerEvents = "auto";
  pathEl.style.fill = "transparent";
  svgEl.appendChild(pathEl);

  if (DEBUG_SAFE_AREA) {
    svgEl.style.backgroundColor = "var(--color-red-10)";
    pathEl.style.fill = "var(--color-red-100)";
  }

  navEl.addEventListener(
    "mousemove",
    _.debounce(
      (event) => {
        const itemEl = event.target.closest(".app-nav__item");
        if (itemEl === null) return;

        /** @type {HTMLElement} */
        const menuEl = itemEl.querySelector(".app-nav__menu");
        const boundingBox = menuEl.getBoundingClientRect();

        if (boundingBox.x <= event.clientX) {
          svgEl.remove();
          return;
        }

        const svgWidth = boundingBox.x - event.clientX;
        const svgHeight = boundingBox.height;

        svgEl.style.width = svgWidth + "px";
        svgEl.style.height = svgHeight + "px";
        svgEl.style.left = event.clientX + "px";
        svgEl.style.top = boundingBox.y + "px";

        pathEl.setAttribute("d", `M 0,${event.clientY - boundingBox.y} L ${svgWidth},0 L ${svgWidth},${svgHeight} z`);

        itemEl.insertAdjacentElement("beforeend", svgEl);
      },
      10,
      { leading: true, trailing: false }
    )
  );

  navEl.addEventListener("mouseleave", () => {
    svgEl.remove();
  });
}

function initMouseNavigation(navEl) {
  navEl.addEventListener("click", (event) => {
    const itemEl = event.target.closest(".app-nav__item");
    if (itemEl === null) return;

    const isOpen = toggleItemOpen(itemEl);
    if (isOpen) {
      // Keyboard activation of a <button> emits a synthetic click with detail === 0.
      // In that case, move focus into the popover so users don't have to Tab past the trigger.
      if (event.detail === 0) {
        focusFirstLinkInMenu(itemEl);
      }
      requestAnimationFrame(() => {
        document.addEventListener("click", () => toggleItemOpen(itemEl, false), { once: true });
      });
    }
  });
}

function initKeyboardNavigation(navEl) {
  navEl.addEventListener("keydown", (event) => {
    const itemEl = event.target.closest(".app-nav__item");
    if (itemEl === null) return;

    const inMenu = event.target.closest(".app-nav__menu") !== null;

    if (inMenu) {
      navigateInMenu(event, itemEl);
    } else {
      navigateInNav(event, itemEl);
    }
  });
}

function navigateInNav(event, itemEl) {
  // Space and Enter are handled natively by the trigger <button> (emits a click).
  // Don't toggle here too — that would double-fire and immediately re-close the popover.
  if (event.code === "ArrowUp") {
    event.preventDefault();
    focusSiblingItem(itemEl, "previousElementSibling");
    return;
  }

  if (event.code === "ArrowDown") {
    event.preventDefault();
    focusSiblingItem(itemEl, "nextElementSibling");
    return;
  }

  if (event.code === "Home") {
    event.preventDefault();
    focusEdgeItem(itemEl, "first");
    return;
  }

  if (event.code === "End") {
    event.preventDefault();
    focusEdgeItem(itemEl, "last");
    return;
  }

  if (event.code === "Escape" && itemEl.classList.contains("app-nav__item--open")) {
    event.preventDefault();
    toggleItemOpen(itemEl, false);
  }
}

function navigateInMenu(event, itemEl) {
  if (event.code === "Escape") {
    event.preventDefault();
    toggleItemOpen(itemEl, false);
    getTrigger(itemEl)?.focus();
    return;
  }

  // Don't hijack arrow keys inside text inputs (e.g. omnisearch) — let the input handle them.
  if (isEditableTarget(event.target)) return;

  if (event.code === "ArrowLeft") {
    event.preventDefault();
    toggleItemOpen(itemEl, false);
    getTrigger(itemEl)?.focus();
    return;
  }

  if (event.code === "ArrowUp") {
    event.preventDefault();
    const focusedEl = getButtonSibling(document.activeElement, "previousElementSibling");
    if (focusedEl !== null) {
      focusedEl.focus();
    } else {
      toggleItemOpen(itemEl, false);
      focusSiblingItem(itemEl, "previousElementSibling");
    }
    return;
  }

  if (event.code === "ArrowDown") {
    event.preventDefault();
    const focusedEl = getButtonSibling(document.activeElement, "nextElementSibling");
    if (focusedEl !== null) {
      focusedEl.focus();
    } else {
      toggleItemOpen(itemEl, false);
      focusSiblingItem(itemEl, "nextElementSibling");
    }
  }
}

const ITEM_SELECTOR = ".app-nav__item, .app-nav__mobile-item";

/**
 * Whether the event target is a text input where the user is editing — in which
 * case the navigation logic should not hijack arrow keys.
 *
 * @param {EventTarget | null} target
 * @returns {boolean}
 */
function isEditableTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}

/**
 * Focus the trigger of the next/previous item in the rail, skipping non-item siblings (e.g. backdrop).
 *
 * @param {HTMLElement} itemEl
 * @param {"previousElementSibling" | "nextElementSibling"} direction
 */
function focusSiblingItem(itemEl, direction) {
  let sibling = itemEl[direction];
  while (sibling && !sibling.matches(ITEM_SELECTOR)) {
    sibling = sibling[direction];
  }
  if (sibling) getTrigger(sibling)?.focus();
}

/**
 * Focus the trigger of the first or last item in the rail.
 *
 * @param {HTMLElement} itemEl
 * @param {"first" | "last"} edge
 */
function focusEdgeItem(itemEl, edge) {
  const list = itemEl.parentElement;
  if (!list) return;
  const items = list.querySelectorAll(ITEM_SELECTOR);
  if (items.length === 0) return;
  const target = edge === "first" ? items[0] : items[items.length - 1];
  getTrigger(target)?.focus();
}

/**
 * Toggle the open state of an item element. Can be forced to open or close.
 * Keeps aria-expanded on the trigger button in sync with the visual state
 * so screen readers announce the disclosure correctly.
 *
 * @param {HTMLElement} itemEl - The item element to toggle
 * @param {boolean} [force] - Force the item to be open or closed
 * @returns {boolean} - The new open state of the item
 */
export function toggleItemOpen(itemEl, force = undefined) {
  const isOpen = itemEl.classList.toggle("app-nav__item--open", force);
  const triggerEl = itemEl.querySelector(".app-nav__trigger");
  if (triggerEl) triggerEl.setAttribute("aria-expanded", String(isOpen));
  return isOpen;
}

/**
 * Get the sibling of the element that is not a divider.
 *
 * @param {HTMLElement} element - The original element
 * @param {"previousElementSibling" | "nextElementSibling"} sibling - The sibling to get
 * @returns {HTMLElement | null}
 */
function getButtonSibling(element, sibling) {
  do {
    element = element[sibling];
  } while (element && element.tagName == "HR");

  return element && element.tagName != "HR" ? element : null;
}

function initActiveSessions() {
  Account.onDisconnect(clearActiveSessions);
  Account.onChangeAcademy(clearActiveSessions);

  const activeSessions = getActiveSessions();
  renderActiveSession(activeSessions);
}

function renderActiveSession(activeSessions) {
  if (activeSessions.length === 0) return;

  const templateEl = document.querySelector(".js-app-nav-session-template");
  const parentEl = document.querySelector(".js-app-nav-active-sessions");

  if (!templateEl || !parentEl) return;

  parentEl.innerHTML = "";
  parentEl.append(createDivider());

  for (const session of activeSessions) {
    const fragmentEl = templateEl.content.cloneNode(true);
    fragmentEl.querySelector(".js-link").href = p`/training_sessions/${session.id}`;
    fragmentEl.querySelector(".js-text").textContent = session.name;
    parentEl.append(fragmentEl);
  }
}

function createDivider() {
  const dividerEl = document.createElement("hr");
  dividerEl.classList.add("divider", "divider--grey");
  return dividerEl;
}

const ACTIVE_SESSIONS_STORAGE_ITEM = "app-nav-active-sessions";

/**
 * Return the active sessions set in local storage.
 *
 * @returns {[{id: number, name: string}] | null}
 */
function getActiveSessions() {
  const stored = localStorage.getItem(ACTIVE_SESSIONS_STORAGE_ITEM);
  if (stored === null) return [];
  const data = JSON.parse(stored);
  return data;
}

/**
 * Set the active session to be diplayed in the app navigation menu.
 * Updates the menu to display the session name and link to the session page.
 *
 * @param {{id: number, name: string}} session
 */
export function pushActiveSession(session) {
  let activeSessions = getActiveSessions();
  activeSessions.unshift(session);
  activeSessions = uniqById(activeSessions);
  // Limit the number of active sessions to 3
  activeSessions.splice(3);

  localStorage.setItem(ACTIVE_SESSIONS_STORAGE_ITEM, JSON.stringify(activeSessions));
  renderActiveSession(activeSessions);
}

function uniqById(sessions) {
  const seen = new Set();
  return sessions.filter((session) => {
    if (seen.has(session.id)) return false;
    seen.add(session.id);
    return true;
  });
}

/**
 * Clear all active sessions from local storage.
 */
function clearActiveSessions() {
  localStorage.removeItem(ACTIVE_SESSIONS_STORAGE_ITEM);
}
